import { useEffect, useRef, useState } from 'react'

export function RaceMultiSelect({
  races,
  selectedIds,
  onChange,
}: {
  races: Array<{ id: number; name: string }>
  selectedIds: Set<number>
  onChange: (ids: Set<number>) => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const toggle = (id: number) => {
    const next = new Set(selectedIds)
    if (next.has(id)) {
      if (next.size === 1) return // always keep at least one selected
      next.delete(id)
    } else {
      next.add(id)
    }
    onChange(next)
  }

  const label = selectedIds.size === races.length
    ? 'All races'
    : selectedIds.size === 1
      ? races.find(r => selectedIds.has(r.id))?.name ?? '1 race'
      : `${selectedIds.size} races`

  if (races.length <= 1) return null

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        onClick={() => setOpen(o => !o)}
        className="text-xs border border-gray-300 rounded px-2 py-0.5 bg-white text-gray-700 hover:bg-gray-50 flex items-center gap-1.5 max-w-[200px]"
      >
        <span className="truncate">{label}</span>
        <span className="text-gray-400 text-[10px]">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-[1100] min-w-[200px] py-1">
          {races.map(r => (
            <label key={r.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedIds.has(r.id)}
                onChange={() => toggle(r.id)}
                className="w-3.5 h-3.5 accent-indigo-600"
              />
              <span className="text-xs text-gray-800 truncate">{r.name}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  )
}
